import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';

const MembershipCard = ({ plan, companyName, expiryDate, cardNumber }) => {
  const { user } = useAuth();
  const getTier = () => {
    const code = (plan?.code || '').toLowerCase();
    if (code.includes('premium')) return 'diamond';
    if (code.includes('standard')) return 'platinum';
    if (code.includes('basic')) return 'gold';
    return 'exclusive'; 
  };

  const tier = getTier();

  // The requested Light Green: #88E788
  const brandGreen = '#88E788';

  const configs = {
    gold: {
      name: 'EXCLUSIVE',
      accent: 'from-amber-400 to-amber-600',
      text: 'text-[#064E3B]',
      bg: brandGreen,
      pattern: 'rgba(255, 255, 255, 0.4)',
      ribbonText: 'text-amber-950',
      divider: 'rgba(6, 78, 59, 0.1)',
    },
    platinum: {
      name: 'EXCLUSIVE',
      accent: 'from-slate-300 to-slate-500',
      text: 'text-[#064E3B]',
      bg: brandGreen,
      pattern: 'rgba(255, 255, 255, 0.3)',
      ribbonText: 'text-slate-900',
      divider: 'rgba(6, 78, 59, 0.1)',
    },
    diamond: {
      name: 'EXCLUSIVE',
      accent: 'from-purple-500 to-purple-700',
      text: 'text-[#064E3B]',
      bg: brandGreen,
      pattern: 'rgba(255, 255, 255, 0.4)',
      ribbonText: 'text-white',
      divider: 'rgba(6, 78, 59, 0.1)',
    },
    exclusive: {
      name: 'EXCLUSIVE',
      accent: 'from-emerald-600 to-emerald-800',
      text: 'text-[#064E3B]',
      bg: brandGreen,
      pattern: 'rgba(255, 255, 255, 0.3)',
      ribbonText: 'text-white',
      divider: 'rgba(6, 78, 59, 0.1)',
    }
  };

  const current = configs[tier] || configs.exclusive;

  const formatExpiry = (dateStr) => {
    if (!dateStr) return 'Not Available';
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`;
  };

  const cleanPlanName = (name) => {
    return (name || "Unknown Plan").replace(/\(Monthly\)|\(Yearly\)/gi, '').trim();
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      style={{ backgroundColor: brandGreen }}
      className={`relative w-full max-w-[450px] aspect-[1.5/1] rounded-3xl overflow-hidden shadow-2xl border border-[#76D176] flex flex-col items-center p-0 group`}
    >
      {/* GEOMETRIC BACKGROUND PATTERN */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-50">
          <div className="absolute top-0 left-0 w-48 h-48 rotate-45 transform -translate-x-12 -translate-y-12" style={{ backgroundColor: current.pattern, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute bottom-0 right-0 w-64 h-64 rotate-12 transform translate-x-24 translate-y-12" style={{ backgroundColor: current.pattern, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }} />
          <div className="absolute top-1/4 right-0 w-24 h-24 opacity-20" style={{ backgroundColor: current.pattern, clipPath: 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)' }} />
          
          {/* Halftone Dot Pattern Simulation */}
          <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.2) 1px, transparent 1px)`, backgroundSize: '16px 16px', opacity: 0.5 }} />
      </div>

      {/* TOP RIBBON: EXCLUSIVE */}
      <div className="relative z-10 w-full flex justify-center mt-6">
          <div className={`relative px-12 py-1 bg-gradient-to-r ${current.accent} ${current.ribbonText} text-[10px] font-black tracking-[0.4em] uppercase shadow-lg`}>
              {current.name}
              {/* Ribbon Tails */}
              <div className={`absolute top-0 -left-3 w-3 h-full bg-inherit`} style={{ clipPath: 'polygon(100% 0, 0 50%, 100% 100%)' }} />
              <div className={`absolute top-0 -right-3 w-3 h-full bg-inherit`} style={{ clipPath: 'polygon(0 0, 100% 50%, 0 100%)' }} />
          </div>
      </div>

      {/* CENTER CONTENT */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full px-12 text-center mt-2">
          <div className="w-full flex items-center justify-center gap-4 mb-3">
              <div className="h-[1px] flex-1 relative" style={{ backgroundColor: current.divider }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#064E3B]/20" />
              </div>
              <div className="w-2 h-2 rotate-45 border border-[#064E3B]/20" />
              <div className="h-[1px] flex-1 relative" style={{ backgroundColor: current.divider }}>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#064E3B]/20" />
              </div>
          </div>
          
          <h2 className="text-2xl font-serif text-[#064E3B] font-bold tracking-[0.2em] uppercase mb-1 drop-shadow-sm">
              Membership Card
          </h2>

          <div className="w-full flex items-center justify-center gap-4">
              <div className="h-[1px] flex-1 relative" style={{ backgroundColor: current.divider }}>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#064E3B]/20" />
              </div>
              <div className="w-2 h-2 rotate-45 border border-[#064E3B]/20" />
              <div className={`h-[1px] flex-1 relative`} style={{ backgroundColor: current.divider }}>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full bg-[#064E3B]/20" />
              </div>
          </div>

          <p className="mt-5 text-sm font-black text-[#064E3B] uppercase tracking-[0.1em] max-w-[300px] truncate">
              {companyName || 'YOUR COMPANY NAME'}
          </p>
      </div>

      {/* BOTTOM INFO BAR */}
      <div className="relative z-10 w-full bg-white/40 backdrop-blur-sm border-t border-[#064E3B]/10 py-3 px-10 flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
              <span className="text-[9px] font-black text-[#064E3B]/60 uppercase tracking-widest">Plan:</span>
              <span className="text-xs font-black text-[#064E3B] tracking-wider">
                  {cleanPlanName(plan?.name)}
              </span>
          </div>
          <div className="flex items-center gap-3">
              <span className="text-[9px] font-black text-[#064E3B]/60 uppercase tracking-widest">Expire Date:</span>
              <span className="text-xs font-black text-[#064E3B] tracking-wider">
                  {formatExpiry(expiryDate)}
              </span>
          </div>
      </div>

      {/* Card Border Bottom Line */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20" />
    </motion.div>
  );
};

export default MembershipCard;
